import { useState, useEffect } from 'react';

const MEDIA_CACHE_NAME = 'totem-media-cache';

export const useCachedMedia = (url: string) => {
  const [cachedUrl, setCachedUrl] = useState<string>(url);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!url) return;

    // Only cache actual http/https URLs (ignore data urls or blob urls)
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      setCachedUrl(url);
      return;
    }

    let isMounted = true;
    let localBlobUrl: string | null = null;

    const fetchAndCache = async () => {
      try {
        setLoading(true);

        if (typeof caches === 'undefined') {
          // Fallback if Cache Storage API is not available
          setCachedUrl(url);
          return;
        }

        const cache = await caches.open(MEDIA_CACHE_NAME);
        
        // Check if resource is already cached
        const cachedResponse = await cache.match(url);
        if (cachedResponse) {
          const blob = await cachedResponse.clone().blob();
          if (isMounted) {
            localBlobUrl = URL.createObjectURL(blob);
            setCachedUrl(localBlobUrl);
          }
          return;
        }

        // If not in cache, fetch it and clone response to save in cache
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch media: ${response.statusText}`);

        // Save in cache
        await cache.put(url, response.clone());

        // Create blob URL for immediate play/display
        const blob = await response.blob();
        if (isMounted) {
          localBlobUrl = URL.createObjectURL(blob);
          setCachedUrl(localBlobUrl);
        }
      } catch (error) {
        console.error('Error caching media:', error, url);
        // Fallback to original URL on failure
        if (isMounted) {
          setCachedUrl(url);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAndCache();

    return () => {
      isMounted = false;
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl);
      }
    };
  }, [url]);

  return { cachedUrl, loading };
};
